/**
 * WorkStoppageDetailsModal
 *
 * One arrêt in full: the V-PLANNING identity of the salarié, the arrêt and what was computed
 * from it, the reimbursements, and the traçabilité.
 *
 * The reimbursements are edited here rather than in the main form because there are many of
 * them per arrêt and they arrive over months — "pour un arrêt il est tout à fait possible de
 * recevoir plusieurs remboursement", said of both the CPAM and the prévoyance. Recording one
 * is the routine act of this screen; it should not require re-opening the arrêt form.
 *
 * The trail below them is read-only by construction: nothing in the UI can edit or delete a
 * history row, which is what makes it worth reading.
 *
 * Drawn in the FormDialog shell of the arrêt form — gradient header, section headings, values
 * in the form's rounded field boxes — so viewing an arrêt and editing it read as one screen.
 *
 * The label/value blocks are a CSS grid on a plain `Box`, not MUI's `<Grid>`: v7 ships the
 * Grid2 implementation under that name and silently ignores `item` / `xs` / `md`, so every
 * cell would collapse to the width of its own text.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Button, Typography, Table, TableBody, TableCell, TableHead, TableRow, IconButton, Chip, Stack,
  Box, TextField, MenuItem, Tooltip,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import Langs, { Localize } from "../../app/lang/langs";
import { showErrorToast, showSuccessToast } from "../../app/utils/helpers";
import NetworkManager from "../../app/managers/networkManager";
import {
  getWorkStoppage, addWorkStoppagePayment, editWorkStoppagePayment,
  deleteWorkStoppagePayment, clearSelectedWorkStoppage,
  getWorkStoppageAttachments, uploadWorkStoppageAttachment, deleteWorkStoppageAttachment,
  clearWorkStoppageAttachments,
} from "../../components/workStoppages/workStoppagesSlice";
import WorkStoppageAttachments from "../../components/workStoppages/WorkStoppageAttachments";
import { StatusChip, statusLabelKey } from "../../components/workStoppages/WorkStoppageStatus";
import FormDialog, { FormSection, FormButton, formFieldSx } from "../../components/popups/FormDialog";
import FormDatePicker from "../../components/inputs/FormDatePicker";

const NAVY = "rgb(0, 40, 128)";

const formatDate = (value) => (value ? moment(value).format("DD/MM/YYYY") : "—");

const formatAmount = (value) =>
  value === null || typeof value === "undefined" || value === ""
    ? "—"
    : new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(value));

const yesNo = (value) =>
  value === null || typeof value === "undefined" ? "—" : Localize(value ? "YES" : "NO");

/**
 * The traçabilité stores raw column names and raw values (`cpam_days_overridden: 1 → 0`).
 * This is the translation key naming each column the API can log; a column missing here
 * falls back to its raw name rather than to nothing.
 */
const HISTORY_FIELD_LABELS = {
  id_planning: "WORK_STOPPAGE_LEAVE",
  id_collab: "COLLAB",
  id_absence_reference: "WORK_STOPPAGE_MOTIF",
  custom_motif: "WORK_STOPPAGE_CUSTOM_MOTIF",
  id_parent: "WORK_STOPPAGE_PARENT",
  stoppage_status: "WORK_STOPPAGE_STATUS",
  start_date: "START_DATE",
  end_date: "END_DATE",
  received_at: "WORK_STOPPAGE_RECEIVED_AT",
  declared_at: "WORK_STOPPAGE_DECLARED_AT",
  subrogation: "WORK_STOPPAGE_SUBROGATION",
  salary_maintenance: "WORK_STOPPAGE_SALARY_MAINTENANCE",
  carence_days: "WORK_STOPPAGE_CARENCE_DAYS",
  cpam_days: "WORK_STOPPAGE_CPAM_DAYS",
  cpam_days_overridden: "WORK_STOPPAGE_CPAM_DAYS_OVERRIDDEN",
  therapeutic_rate: "WORK_STOPPAGE_THERAPEUTIC_RATE",
  maintenance_category: "WORK_STOPPAGE_MAINTENANCE_CATEGORY",
  maintenance_days_100: "WORK_STOPPAGE_MAINTENANCE_100",
  maintenance_days_80: "WORK_STOPPAGE_MAINTENANCE_REDUCED",
  maintenance_days_unpaid: "WORK_STOPPAGE_MAINTENANCE_UNPAID",
  maintenance_overridden: "WORK_STOPPAGE_MAINTENANCE_OVERRIDDEN",
  cpam_reimbursement_status: "WORK_STOPPAGE_CPAM_REIMBURSEMENT_STATUS",
  prevoyance_reimbursement_status: "WORK_STOPPAGE_PREVOYANCE_REIMBURSEMENT_STATUS",
  // Not a column: logged when declaring the suivi validates a collab's pending request.
  planning_status: "WORK_STOPPAGE_PLANNING_STATUS",
  comments: "Comments",
  // Payment rows
  source: "WORK_STOPPAGE_PAYMENT_SOURCE",
  payment_date: "WORK_STOPPAGE_PAYMENT_DATE",
  period_from: "WORK_STOPPAGE_PERIOD_FROM",
  period_to: "WORK_STOPPAGE_PERIOD_TO",
  amount: "WORK_STOPPAGE_AMOUNT",
};

const HISTORY_BOOLEAN_FIELDS = ["subrogation", "salary_maintenance", "cpam_days_overridden", "maintenance_overridden"];
const HISTORY_DATE_FIELDS = ["start_date", "end_date", "received_at", "declared_at", "payment_date", "period_from", "period_to"];

/**
 * One logged value, as a person reads it. The API writes values as strings, NULL as null.
 * @param {Array} reference - the référentiel, to name a motif rather than show its id
 */
const formatHistoryValue = (field, value, reference) => {
  if (value === null || typeof value === "undefined" || value === "") return "—";
  if (HISTORY_BOOLEAN_FIELDS.includes(field)) return Localize(Number(value) ? "YES" : "NO");
  if (HISTORY_DATE_FIELDS.includes(field)) return formatDate(value);
  if (field === "id_absence_reference") {
    const motif = (reference || []).find((row) => String(row.id) === String(value));
    return motif ? motif.label : `#${value}`;
  }
  if (field === "id_parent" || field === "id_planning") return `#${value}`;
  if (field === "stoppage_status" || field === "cpam_reimbursement_status" || field === "prevoyance_reimbursement_status")
    return Localize(statusLabelKey(value));
  if (field === "source") return value === "PREVOYANCE" ? Localize("WORK_STOPPAGE_PREVOYANCE") : "CPAM";
  if (field === "planning_status") return Localize(`WORK_STOPPAGE_LEAVE_STATUS_${value}`);
  if (field === "amount") return formatAmount(value);
  if (field === "therapeutic_rate") return `${Number(value)} %`;
  return value;
};

/**
 * One label/value pair: the label as the form writes it, the value in the form's field box,
 * read-only.
 */
function Field({ label, children, full }) {
  return (
    <Box sx={{ gridColumn: full ? "1 / -1" : "auto", minWidth: 0 }}>
      {label ? (
        <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: NAVY, letterSpacing: "-0.01em", mb: 0.5 }}>
          {label}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minHeight: 40,
          px: 1.5,
          py: 0.75,
          borderRadius: "10px",
          border: "1.5px solid rgba(0, 40, 128, 0.1)",
          bgcolor: "rgba(0, 40, 128, 0.02)",
          fontSize: "0.9rem",
          color: "rgba(0, 0, 0, 0.87)",
          wordBreak: "break-word",
        }}
      >
        {children === null || typeof children === "undefined" || children === "" ? "—" : children}
      </Box>
    </Box>
  );
}

/** The grid those pairs sit in: two columns on a phone, four from tablet up. */
const fieldGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
  gap: 1.5,
  px: 1,
  py: 1,
};

/** The payment and history tables, framed like the form's inputs. */
const tableFrameSx = {
  mx: 1,
  mb: 1,
  border: "1.5px solid rgba(0, 40, 128, 0.08)",
  borderRadius: "12px",
  overflowX: "auto",
  "& .MuiTableCell-head": {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: NAVY,
    bgcolor: "rgba(0, 40, 128, 0.03)",
  },
  "& .MuiTableRow-root:last-of-type .MuiTableCell-body": { borderBottom: "none" },
};

const emptyPayment = { source: "CPAM", payment_date: "", period_from: "", period_to: "", amount: "", comments: "" };

/**
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {Function} props.onHide
 * @param {Object} props.stoppage - the row clicked in the grid (used until the full one loads)
 * @param {boolean} props.canEdit - whether workStoppages:edit is held
 * @param {Function} props.onChanged - called after a payment write, to refresh the table
 * @param {Function} [props.onEdit] - (row) => void, opens the arrêt form on this arrêt
 */
export default function WorkStoppageDetailsModal({ visible, onHide, stoppage, canEdit, onChanged, onEdit }) {
  const dispatch = useDispatch();
  const isMobile = useSelector((state) => state.settings.isMobile);
  const selected = useSelector((state) => state.workStoppages.selected);
  const history = useSelector((state) => state.workStoppages.selectedHistory);
  const reference = useSelector((state) => state.workStoppages.reference);
  const attachments = useSelector((state) => state.workStoppages.attachments);
  const attachmentsLoading = useSelector((state) => state.workStoppages.attachmentsLoading);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  // Justificatifs are medical documents: their routes require workStoppages EDIT even to
  // list them, so a view-only reader never asks (and never sees an error for it).
  const idPlanning = stoppage?.id_planning;
  useEffect(() => {
    if (visible && canEdit && idPlanning) dispatch(getWorkStoppageAttachments(idPlanning));
    if (!visible) dispatch(clearWorkStoppageAttachments());
  }, [visible, canEdit, idPlanning, dispatch]);

  const addAttachment = async (file) => {
    setUploadingAttachment(true);
    try {
      const response = await dispatch(uploadWorkStoppageAttachment({ file, idPlanning }));
      if (response?.payload?.responseData?.status === 1) {
        showSuccessToast(Localize("WORK_STOPPAGE_ATTACHMENT_SAVED"));
        dispatch(getWorkStoppageAttachments(idPlanning));
      } else {
        showErrorToast(response?.payload?.responseData?.error || Localize("WORK_STOPPAGE_ATTACHMENT_FAILED"));
      }
    } finally {
      setUploadingAttachment(false);
    }
  };

  const removeAttachment = async (item) => {
    const response = await dispatch(deleteWorkStoppageAttachment({ id: item.id }));
    if (response?.payload?.responseData?.status === 1) {
      showSuccessToast(Localize("WORK_STOPPAGE_ATTACHMENT_DELETED"));
      dispatch(getWorkStoppageAttachments(idPlanning));
    } else {
      showErrorToast(response?.payload?.responseData?.message || Localize("WORK_STOPPAGE_SAVE_FAILED"));
    }
  };

  // The clicked row is shown immediately and replaced by the full record once it arrives, so
  // the dialog never opens empty on a slow connection.
  const row = useMemo(
    () => (selected && stoppage && selected.id === stoppage.id ? selected : stoppage),
    [selected, stoppage]
  );

  /** null = no editor open; 0 = the "new payment" row; otherwise the payment id. */
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState(emptyPayment);

  useEffect(() => {
    if (visible && stoppage?.id) dispatch(getWorkStoppage({ id: stoppage.id }));
    if (!visible) {
      dispatch(clearSelectedWorkStoppage());
      setEditingPayment(null);
    }
  }, [visible, stoppage, dispatch]);

  const startNewPayment = () => {
    setPaymentForm({ ...emptyPayment });
    setEditingPayment(0);
  };

  const startEditPayment = (payment) => {
    setPaymentForm({
      source: payment.source,
      payment_date: payment.payment_date ? moment(payment.payment_date).format("YYYY-MM-DD") : "",
      period_from: payment.period_from ? moment(payment.period_from).format("YYYY-MM-DD") : "",
      period_to: payment.period_to ? moment(payment.period_to).format("YYYY-MM-DD") : "",
      amount: payment.amount ?? "",
      comments: payment.comments || "",
    });
    setEditingPayment(payment.id);
  };

  const savePayment = async () => {
    if (!paymentForm.source || paymentForm.amount === "") {
      showErrorToast(Localize("WORK_STOPPAGE_REQUIRED"));
      return;
    }
    const body = {
      source: paymentForm.source,
      payment_date: paymentForm.payment_date || null,
      period_from: paymentForm.period_from || null,
      period_to: paymentForm.period_to || null,
      amount: paymentForm.amount,
      comments: paymentForm.comments || null,
    };
    const action = editingPayment
      ? editWorkStoppagePayment({ ...body, id: editingPayment })
      : addWorkStoppagePayment({ ...body, id_work_stoppage: row.id });
    const response = await dispatch(action);
    const data = response?.payload?.responseData;
    if (data && data.status === 1) {
      showSuccessToast(Localize("WORK_STOPPAGE_PAYMENT_SAVED"));
      setEditingPayment(null);
      dispatch(getWorkStoppage({ id: row.id }));
      onChanged();
    } else {
      showErrorToast(data?.message || data?.error || Localize("WORK_STOPPAGE_SAVE_FAILED"));
    }
  };

  const removePayment = async (payment) => {
    const response = await dispatch(deleteWorkStoppagePayment({ id: payment.id }));
    const data = response?.payload?.responseData;
    if (data && data.status === 1) {
      showSuccessToast(Localize("WORK_STOPPAGE_PAYMENT_DELETED"));
      dispatch(getWorkStoppage({ id: row.id }));
      onChanged();
    } else {
      showErrorToast(data?.message || Localize("WORK_STOPPAGE_SAVE_FAILED"));
    }
  };

  if (!row) return null;

  const payments = row.payments || [];

  /** The editor row, shared by "new" and "edit" so the two can never drift apart. */
  const paymentEditor = (
    <TableRow>
      <TableCell>
        <TextField
          select size="small" fullWidth sx={formFieldSx}
          value={paymentForm.source}
          onChange={(e) => setPaymentForm((p) => ({ ...p, source: e.target.value }))}
        >
          <MenuItem value="CPAM">CPAM</MenuItem>
          <MenuItem value="PREVOYANCE">{Localize("WORK_STOPPAGE_PREVOYANCE")}</MenuItem>
        </TextField>
      </TableCell>
      <TableCell>
        <FormDatePicker
          clearable
          value={paymentForm.payment_date}
          onChange={(date) => setPaymentForm((p) => ({ ...p, payment_date: date }))}
          sx={{ minWidth: 150 }}
        />
      </TableCell>
      <TableCell>
        <FormDatePicker
          clearable
          value={paymentForm.period_from}
          onChange={(date) => setPaymentForm((p) => ({ ...p, period_from: date }))}
          sx={{ minWidth: 150 }}
        />
      </TableCell>
      <TableCell>
        <FormDatePicker
          clearable
          value={paymentForm.period_to} minDate={paymentForm.period_from}
          onChange={(date) => setPaymentForm((p) => ({ ...p, period_to: date }))}
          sx={{ minWidth: 150 }}
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small" type="number" fullWidth sx={formFieldSx}
          value={paymentForm.amount}
          onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))}
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small" fullWidth sx={formFieldSx}
          value={paymentForm.comments}
          onChange={(e) => setPaymentForm((p) => ({ ...p, comments: e.target.value }))}
        />
      </TableCell>
      <TableCell align="right">
        <IconButton size="small" color="success" onClick={savePayment}><CheckRoundedIcon fontSize="small" /></IconButton>
        <IconButton size="small" onClick={() => setEditingPayment(null)}><CloseRoundedIcon fontSize="small" /></IconButton>
      </TableCell>
    </TableRow>
  );

  return (
    <FormDialog
      open={visible}
      onClose={onHide}
      title={<><Langs str="WORK_STOPPAGE_DETAILS" />{` — ${row.prenom || ""} ${row.nom || ""}`}</>}
      titleAdornment={<StatusChip status={row.stoppage_status === "PROLONGATION" ? "PROLONGATION" : "INITIAL"} />}
      maxWidth="lg"
      fullScreen={isMobile === 2}
      actions={(
        <>
          <FormButton variant="secondary" onClick={onHide}><Langs str="CLOSE" /></FormButton>
          {canEdit && onEdit && (
            <FormButton startIcon={<EditRoundedIcon />} onClick={() => onEdit(row)}>
              <Langs str="WORK_STOPPAGE_EDIT" />
            </FormButton>
          )}
        </>
      )}
    >
        {/* ── V-PLANNING identity ───────────────────────────────────────── */}
        <FormSection><Langs str="WORK_STOPPAGE_INFO" /></FormSection>
        <Box sx={fieldGridSx}>
          <Field label={Localize("WORK_STOPPAGE_NUM_SS")}>{row.num_ss}</Field>
          <Field label={Localize("WORK_STOPPAGE_ENTITY")}>{row.entite}</Field>
          <Field label={Localize("WORK_STOPPAGE_SITE")}>{row.site || row.team_name}</Field>
          <Field label={Localize("WORK_STOPPAGE_EMPLOYEE_STATUS")}>{row.statut}</Field>
          <Field label={Localize("WORK_STOPPAGE_SENIORITY_DATE")}>{formatDate(row.date_anciennete)}</Field>
          <Field label={Localize("WORK_STOPPAGE_EXIT_DATE")}>{formatDate(row.date_sortie)}</Field>
        </Box>

        {/* ── The arrêt ─────────────────────────────────────────────────── */}
        <FormSection><Langs str="WORK_STOPPAGES" /></FormSection>
        <Box sx={fieldGridSx}>
          <Field label={Localize("WORK_STOPPAGE_LEAVE_TYPE")}>
            {[row.planning_type, row.planning_leave_label].filter(Boolean).join(" - ")}
          </Field>
          <Field label={Localize("WORK_STOPPAGE_REF_CATEGORY")}>{row.motif_category}</Field>
          <Field label={Localize("WORK_STOPPAGE_MOTIF")}>{row.motif}</Field>
          <Field label={Localize("WORK_STOPPAGE_RECEIVED_AT")}>{formatDate(row.received_at)}</Field>
          <Field label={Localize("WORK_STOPPAGE_DECLARED_AT")}>{formatDate(row.declared_at)}</Field>
          <Field label={Localize("START_DATE")}>{formatDate(row.start_date)}</Field>
          <Field label={Localize("END_DATE")}>{formatDate(row.end_date)}</Field>
          <Field label={Localize("WORK_STOPPAGE_SUBROGATION")}>{yesNo(row.subrogation)}</Field>
          <Field label={Localize("WORK_STOPPAGE_SALARY_MAINTENANCE")}>{yesNo(row.salary_maintenance)}</Field>
          <Field label={Localize("WORK_STOPPAGE_CARENCE_DAYS")}>{row.carence_days}</Field>
          <Field label={Localize("WORK_STOPPAGE_CPAM_DAYS")}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <span>{row.cpam_days ?? "—"}</span>
              {row.cpam_days_overridden ? (
                <Tooltip title={Localize("WORK_STOPPAGE_MANUAL")}>
                  <Chip size="small" label="M" sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }} />
                </Tooltip>
              ) : null}
            </Stack>
          </Field>
          <Field label={Localize("WORK_STOPPAGE_ABSENCE_DAYS")}>{row.absence_days}</Field>
          <Field label={Localize("WORK_STOPPAGE_THERAPEUTIC_RATE")}>
            {row.therapeutic_rate === null || typeof row.therapeutic_rate === "undefined" ? "—" : `${Number(row.therapeutic_rate)} %`}
          </Field>
        </Box>

        {/* ── Maintien de salaire (Syntec) ──────────────────────────────── */}
        <FormSection><Langs str="WORK_STOPPAGE_MAINTENANCE" /></FormSection>
        <Box sx={fieldGridSx}>
          <Field label={Localize("WORK_STOPPAGE_MAINTENANCE_CATEGORY")}>{row.maintenance_category}</Field>
          <Field label={Localize("WORK_STOPPAGE_MAINTENANCE_100")}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <span>{row.maintenance_days_100 ?? "—"}</span>
              {row.maintenance_overridden ? (
                <Tooltip title={Localize("WORK_STOPPAGE_MANUAL")}>
                  <Chip size="small" label="M" sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }} />
                </Tooltip>
              ) : null}
            </Stack>
          </Field>
          <Field label={`${Localize("WORK_STOPPAGE_MAINTENANCE_REDUCED")}${row.maintenance_partial_rate ? ` (${Number(row.maintenance_partial_rate)} %)` : ""}`}>
            {row.maintenance_days_80 ?? "—"}
          </Field>
          <Field label={Localize("WORK_STOPPAGE_MAINTENANCE_UNPAID")}>{row.maintenance_days_unpaid ?? "—"}</Field>
          {/* NULL across the board means the schedule does not cover this motif at all (an AT
              or a congé maternité is maintained under its own terms) — said in words, because
              three empty cells otherwise read as "nobody filled this in". */}
          {row.maintenance_days_100 === null && row.maintenance_days_80 === null ? (
            <Typography sx={{ gridColumn: "1 / -1", color: "rgba(0, 40, 128, 0.5)", fontSize: "0.8rem" }}>
              <Langs str="WORK_STOPPAGE_MAINTENANCE_NOT_APPLICABLE" />
            </Typography>
          ) : null}
        </Box>

        {row.comments ? (
          <Box sx={fieldGridSx}>
            <Field label={Localize("Comments")} full>{row.comments}</Field>
          </Box>
        ) : null}

        {/* ── Remboursements ────────────────────────────────────────────── */}
        <FormSection><Langs str="WORK_STOPPAGE_PAYMENTS" /></FormSection>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: 1, px: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">
              {`${Localize("WORK_STOPPAGE_CPAM_TOTAL")}: ${formatAmount(row.cpam_total)}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {`${Localize("WORK_STOPPAGE_PREVOYANCE_TOTAL")}: ${formatAmount(row.prevoyance_total)}`}
            </Typography>
            {canEdit && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<AddRoundedIcon />}
                onClick={startNewPayment}
                disabled={editingPayment === 0}
                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
              >
                <Langs str="WORK_STOPPAGE_PAYMENT_ADD" />
              </Button>
            )}
          </Stack>
        </Box>

        {/* Whether each organisme has settled the arrêt — the answer the payments below
            cannot give on their own. Changed from the arrêt form. */}
        <Box sx={fieldGridSx}>
          <Field label={Localize("WORK_STOPPAGE_CPAM_REIMBURSEMENT_STATUS")}>
            {row.cpam_reimbursement_status ? <StatusChip status={row.cpam_reimbursement_status} /> : null}
          </Field>
          <Field label={Localize("WORK_STOPPAGE_PREVOYANCE_REIMBURSEMENT_STATUS")}>
            {row.prevoyance_reimbursement_status ? <StatusChip status={row.prevoyance_reimbursement_status} /> : null}
          </Field>
        </Box>

        <Box sx={tableFrameSx}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{Localize("WORK_STOPPAGE_PAYMENT_SOURCE")}</TableCell>
              <TableCell>{Localize("WORK_STOPPAGE_PAYMENT_DATE")}</TableCell>
              <TableCell>{Localize("WORK_STOPPAGE_PERIOD_FROM")}</TableCell>
              <TableCell>{Localize("WORK_STOPPAGE_PERIOD_TO")}</TableCell>
              <TableCell>{Localize("WORK_STOPPAGE_AMOUNT")}</TableCell>
              <TableCell>{Localize("Comments")}</TableCell>
              <TableCell align="right" width={96} />
            </TableRow>
          </TableHead>
          <TableBody>
            {editingPayment === 0 && paymentEditor}
            {payments.map((payment) => (
              editingPayment === payment.id ? (
                <React.Fragment key={payment.id}>{paymentEditor}</React.Fragment>
              ) : (
                <TableRow key={payment.id}>
                  <TableCell>
                    <Chip
                      size="small"
                      label={payment.source === "PREVOYANCE" ? Localize("WORK_STOPPAGE_PREVOYANCE") : "CPAM"}
                      color={payment.source === "PREVOYANCE" ? "info" : "success"}
                      sx={{ fontWeight: 600, borderRadius: "8px" }}
                    />
                  </TableCell>
                  <TableCell>{formatDate(payment.payment_date)}</TableCell>
                  <TableCell>{formatDate(payment.period_from)}</TableCell>
                  <TableCell>{formatDate(payment.period_to)}</TableCell>
                  <TableCell>{formatAmount(payment.amount)}</TableCell>
                  <TableCell>{payment.comments || "—"}</TableCell>
                  <TableCell align="right">
                    {canEdit && (
                      <>
                        <IconButton size="small" onClick={() => startEditPayment(payment)}>
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => removePayment(payment)}>
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              )
            ))}
            {!payments.length && editingPayment !== 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.6 }}>
                    <Langs str="WORK_STOPPAGE_NO_PAYMENTS" />
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </Box>

        {/* ── Justificatifs ─────────────────────────────────────────────── */}
        {canEdit && row.id_planning ? (
          <>
            <FormSection><Langs str="WORK_STOPPAGE_JUSTIFICATIFS" /></FormSection>
            <Box sx={{ px: 1, py: 1 }}>
              <WorkStoppageAttachments
                items={(attachments || []).map((attachment) => ({
                  key: attachment.id,
                  id: attachment.id,
                  name: attachment.original_name,
                  size: attachment.size,
                  url: NetworkManager.getInstance().getWorkStoppageAttachmentUrl(attachment.id),
                }))}
                loading={attachmentsLoading}
                busy={uploadingAttachment}
                canEdit={canEdit}
                onAdd={addAttachment}
                onRemove={removeAttachment}
                titleKey={null}
              />
            </Box>
          </>
        ) : null}

        {/* ── Traçabilité ───────────────────────────────────────────────── */}
        <FormSection><Langs str="WORK_STOPPAGE_HISTORY" /></FormSection>
        <Box sx={tableFrameSx}>
        <Table size="small">
          <TableBody>
            {(history || []).map((entry) => (
              <TableRow key={entry.id}>
                <TableCell width={160}>
                  <Typography variant="caption" color="text.secondary">
                    {moment(entry.created_at).format("DD/MM/YYYY HH:mm")}
                  </Typography>
                </TableCell>
                <TableCell width={200}>
                  <Chip
                    size="small"
                    label={Localize(`WORK_STOPPAGE_ACTION_${entry.action}`)}
                    sx={{ fontWeight: 600, borderRadius: "8px" }}
                  />
                </TableCell>
                <TableCell>
                  {entry.field ? (
                    <>
                      <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                        {HISTORY_FIELD_LABELS[entry.field] ? Localize(HISTORY_FIELD_LABELS[entry.field]) : entry.field}
                      </Typography>
                      {` : ${formatHistoryValue(entry.field, entry.old_value, reference)}`}
                      {` → ${formatHistoryValue(entry.field, entry.new_value, reference)}`}
                    </>
                  ) : ""}
                </TableCell>
                <TableCell width={180}>
                  <Typography variant="caption" color="text.secondary">{entry.createdBy}</Typography>
                </TableCell>
              </TableRow>
            ))}
            {!(history || []).length && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.6 }}>
                    <Langs str="WORK_STOPPAGE_NO_HISTORY" />
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </Box>
    </FormDialog>
  );
}
